/**
 * SwingJournal — Trade Journal & Performance Analytics
 * Computes all stats client-side from EXITED positions.
 * No new backend endpoint needed.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { getSwingPositions } from '../../api/client';

// ── Small helpers ─────────────────────────────────────────────────────────────

function StatTile({ label, value, sub, color = '#fff' }) {
  return (
    <div className="card" style={{ minWidth: 130, flex: '1 1 130px', textAlign: 'center', padding: '16px 12px' }}>
      <div style={{ color: '#888', fontSize: 11, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      {sub && <div style={{ color: '#555', fontSize: 11, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function WinLossBar({ wins, losses }) {
  const total = wins + losses;
  if (!total) return null;
  const winPct = (wins / total) * 100;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', height: 8 }}>
        <div style={{ width: `${winPct}%`, background: '#4CAF50', transition: 'width 0.4s' }} />
        <div style={{ flex: 1, background: '#f44336' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#888', marginTop: 3 }}>
        <span style={{ color: '#4CAF50' }}>{wins}W</span>
        <span style={{ color: '#f44' }}>{losses}L</span>
      </div>
    </div>
  );
}

// ── Equity curve tooltip ──────────────────────────────────────────────────────

function EquityTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  return (
    <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ color: '#888', marginBottom: 4 }}>{label}</div>
      <div style={{ color: val >= 0 ? '#4CAF50' : '#f44', fontWeight: 700 }}>
        {val >= 0 ? '+' : ''}₹{Math.round(Math.abs(val)).toLocaleString('en-IN')}
      </div>
    </div>
  );
}

// ── Compute stats from closed trades ─────────────────────────────────────────

function computeStats(trades) {
  if (!trades.length) return null;

  const withPnl = trades.filter(t => t.realised_pnl != null);
  if (!withPnl.length) return null;

  const winners  = withPnl.filter(t => t.realised_pnl > 0);
  const losers   = withPnl.filter(t => t.realised_pnl < 0);
  const breakEvn = withPnl.filter(t => t.realised_pnl === 0);

  const totalPnl    = withPnl.reduce((s, t) => s + t.realised_pnl, 0);
  const grossWin    = winners.reduce((s, t) => s + t.realised_pnl, 0);
  const grossLoss   = Math.abs(losers.reduce((s, t) => s + t.realised_pnl, 0));
  const profitFactor = grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : 0;

  const avgWin  = winners.length ? grossWin  / winners.length : 0;
  const avgLoss = losers.length  ? grossLoss / losers.length  : 0;
  const rrRatio = avgLoss > 0 ? avgWin / avgLoss : null;

  // Average hold days
  const holdDays = withPnl
    .filter(t => t.entry_date && t.exit_date)
    .map(t => Math.round((new Date(t.exit_date) - new Date(t.entry_date)) / 86400000));
  const avgHold = holdDays.length ? Math.round(holdDays.reduce((a, b) => a + b, 0) / holdDays.length) : null;

  // Best and worst
  const best  = [...withPnl].sort((a, b) => b.realised_pnl - a.realised_pnl)[0];
  const worst = [...withPnl].sort((a, b) => a.realised_pnl - b.realised_pnl)[0];

  // Equity curve: sorted by exit date, cumulative P&L
  const sorted = [...withPnl]
    .filter(t => t.exit_date)
    .sort((a, b) => new Date(a.exit_date) - new Date(b.exit_date));
  let cum = 0;
  const equityCurve = sorted.map(t => {
    cum += t.realised_pnl;
    return {
      date: new Date(t.exit_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
      pnl: Math.round(cum),
      symbol: t.symbol,
    };
  });

  return {
    total: withPnl.length,
    winners: winners.length,
    losers: losers.length,
    breakEvn: breakEvn.length,
    winRate: withPnl.length ? ((winners.length / withPnl.length) * 100).toFixed(1) : '—',
    totalPnl,
    grossWin,
    grossLoss,
    profitFactor: isFinite(profitFactor) ? profitFactor.toFixed(2) : '∞',
    avgWin: Math.round(avgWin),
    avgLoss: Math.round(avgLoss),
    rrRatio: rrRatio ? rrRatio.toFixed(2) : '—',
    avgHold,
    best,
    worst,
    equityCurve,
  };
}

// ── Main component ────────────────────────────────────────────────────────────

export default function SwingJournal() {
  const [trades, setTrades]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSwingPositions({ status: 'EXITED' });
      setTrades(res.data.positions || []);
    } catch {
      setError('Failed to load trade history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 32, color: '#888', textAlign: 'center' }}>Loading trade history...</div>;
  if (error)   return <div style={{ color: '#f44', padding: 16, background: '#2a1010', borderRadius: 6 }}>{error}</div>;

  const stats = computeStats(trades);
  const displayed = showAll ? trades : trades.slice(0, 20);

  return (
    <div>
      {/* Stats tiles */}
      {!stats ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📒</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No closed trades yet</div>
          <div style={{ fontSize: 13, color: '#555' }}>Exit your first position to start tracking performance.</div>
        </div>
      ) : (
        <>
          {/* Top stats */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
            <StatTile
              label="Total Trades" value={stats.total}
              sub={<WinLossBar wins={stats.winners} losses={stats.losers} />}
            />
            <StatTile
              label="Win Rate" value={`${stats.winRate}%`}
              sub={`${stats.winners}W · ${stats.losers}L · ${stats.breakEvn}BE`}
              color={parseFloat(stats.winRate) >= 50 ? '#4CAF50' : '#f44'}
            />
            <StatTile
              label="Total P&L"
              value={`${stats.totalPnl >= 0 ? '+' : ''}₹${Math.round(Math.abs(stats.totalPnl)).toLocaleString('en-IN')}`}
              color={stats.totalPnl >= 0 ? '#4CAF50' : '#f44'}
            />
            <StatTile label="Profit Factor" value={stats.profitFactor} sub="gross win / gross loss" color={parseFloat(stats.profitFactor) >= 1.5 ? '#4CAF50' : parseFloat(stats.profitFactor) >= 1 ? '#FF9800' : '#f44'} />
            <StatTile label="Avg Win"  value={`+₹${stats.avgWin.toLocaleString('en-IN')}`}  color="#4CAF50" />
            <StatTile label="Avg Loss" value={`-₹${stats.avgLoss.toLocaleString('en-IN')}`} color="#f44" />
            <StatTile label="Avg R:R"  value={stats.rrRatio} sub="avg win / avg loss" color={parseFloat(stats.rrRatio) >= 1.5 ? '#4CAF50' : '#FF9800'} />
            {stats.avgHold && <StatTile label="Avg Hold" value={`${stats.avgHold}d`} sub="calendar days" />}
          </div>

          {/* Best / Worst trades */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            {stats.best && (
              <div className="card" style={{ flex: 1, minWidth: 200, borderLeft: '3px solid #4CAF50' }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>🏆 BEST TRADE</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 700 }}>{stats.best.symbol}</div>
                <div style={{ color: '#4CAF50', fontWeight: 700, fontSize: 18 }}>
                  +₹{Math.round(stats.best.realised_pnl).toLocaleString('en-IN')}
                  {stats.best.pnl_pct != null && <span style={{ fontSize: 12, marginLeft: 6 }}>({stats.best.pnl_pct.toFixed(1)}%)</span>}
                </div>
              </div>
            )}
            {stats.worst && stats.worst.realised_pnl < 0 && (
              <div className="card" style={{ flex: 1, minWidth: 200, borderLeft: '3px solid #f44' }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>📉 WORST TRADE</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 700 }}>{stats.worst.symbol}</div>
                <div style={{ color: '#f44', fontWeight: 700, fontSize: 18 }}>
                  -₹{Math.round(Math.abs(stats.worst.realised_pnl)).toLocaleString('en-IN')}
                  {stats.worst.pnl_pct != null && <span style={{ fontSize: 12, marginLeft: 6 }}>({stats.worst.pnl_pct.toFixed(1)}%)</span>}
                </div>
              </div>
            )}
          </div>

          {/* Equity curve */}
          {stats.equityCurve.length >= 2 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Equity Curve</span>
                <span style={{ color: stats.totalPnl >= 0 ? '#4CAF50' : '#f44', fontSize: 13, fontWeight: 700 }}>
                  {stats.totalPnl >= 0 ? '+' : ''}₹{Math.round(stats.totalPnl).toLocaleString('en-IN')} cumulative
                </span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={stats.equityCurve} margin={{ top: 4, right: 12, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                  <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis
                    tick={{ fill: '#666', fontSize: 10 }} tickLine={false} axisLine={false}
                    tickFormatter={v => `₹${Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
                  />
                  <ReferenceLine y={0} stroke="#444" strokeDasharray="4 2" />
                  <Tooltip content={<EquityTooltip />} />
                  <Line
                    type="monotone" dataKey="pnl" stroke={stats.totalPnl >= 0 ? '#4CAF50' : '#f44'}
                    strokeWidth={2} dot={{ r: 3, fill: stats.totalPnl >= 0 ? '#4CAF50' : '#f44' }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}

      {/* Trade log table */}
      {trades.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #333', fontWeight: 600, fontSize: 14 }}>
            Trade Log ({trades.length} closed trades)
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#141414', borderBottom: '1px solid #333' }}>
                  {['Symbol', 'Sector', 'Entry Date', 'Exit Date', 'Entry ₹', 'Exit ₹', 'Qty', 'Hold Days', 'P&L ₹', 'P&L %', 'Tags'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayed.map(t => {
                  const holdDays = t.entry_date && t.exit_date
                    ? Math.round((new Date(t.exit_date) - new Date(t.entry_date)) / 86400000)
                    : null;
                  const pnl = t.realised_pnl;
                  const pnlColor = pnl > 0 ? '#4CAF50' : pnl < 0 ? '#f44' : '#aaa';
                  return (
                    <tr key={t.id} style={{ borderBottom: '1px solid #1a1a1a', background: pnl > 0 ? 'rgba(76,175,80,0.03)' : pnl < 0 ? 'rgba(244,67,54,0.03)' : 'transparent' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 700, fontFamily: 'monospace' }}>{t.symbol}</td>
                      <td style={{ padding: '8px 12px', color: '#888', fontSize: 12 }}>{t.sector || '—'}</td>
                      <td style={{ padding: '8px 12px', color: '#888', fontSize: 12 }}>
                        {t.entry_date ? new Date(t.entry_date).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', color: '#888', fontSize: 12 }}>
                        {t.exit_date ? new Date(t.exit_date).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', fontVariantNumeric: 'tabular-nums' }}>
                        {t.entry_price ? `₹${parseFloat(t.entry_price).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', fontVariantNumeric: 'tabular-nums' }}>
                        {t.exit_price ? `₹${parseFloat(t.exit_price).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td style={{ padding: '8px 12px' }}>{t.quantity || '—'}</td>
                      <td style={{ padding: '8px 12px', color: '#888' }}>{holdDays != null ? `${holdDays}d` : '—'}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 600, color: pnlColor, fontVariantNumeric: 'tabular-nums' }}>
                        {pnl != null ? `${pnl >= 0 ? '+' : ''}₹${Math.round(Math.abs(pnl)).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', color: pnlColor, fontSize: 12 }}>
                        {t.pnl_pct != null ? `${t.pnl_pct >= 0 ? '+' : ''}${t.pnl_pct.toFixed(2)}%` : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', fontSize: 11, color: '#666' }}>
                        {t.setup_tags || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {trades.length > 20 && (
            <div style={{ padding: '10px 16px', borderTop: '1px solid #222', textAlign: 'center' }}>
              <button
                onClick={() => setShowAll(v => !v)}
                style={{ background: 'none', border: 'none', color: '#4CAF50', cursor: 'pointer', fontSize: 13 }}
              >
                {showAll ? '▲ Show less' : `▼ Show all ${trades.length} trades`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
